import {Box} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, {createContext, useContext} from "react";
import {displayFontFamily} from "./signingTheme";

export interface RowTone {
    label: string;
    rule: string;
}

const RowToneContext = createContext<RowTone | undefined>(undefined);

export const RowToneProvider = RowToneContext.Provider;

export const useRowTone = (): RowTone => {
    const theme = useTheme();
    return useContext(RowToneContext) ?? {label: theme.palette.text.secondary, rule: theme.palette.divider};
};

const AMOUNT = {fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap'} as const;
const KEEP_WHOLE = 24;

export function Parts({parts}: {parts: (string | false | undefined)[]}) {
    const shown = parts.filter((part): part is string => !!part);

    return (
        <>
            {shown.map((part, index) => (
                <React.Fragment key={index}>
                    {index > 0 && ' · '}
                    <Box component={'span'} sx={part.length <= KEEP_WHOLE ? {whiteSpace: 'nowrap'} : undefined}>
                        {part}
                    </Box>
                </React.Fragment>
            ))}
        </>
    );
}

export function SummaryRow({label, value, caption, amount}: {
    label: string;
    value: React.ReactNode;
    caption?: React.ReactNode;
    amount?: boolean;
}) {
    const tone = useRowTone();

    return (
        <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: {xs: 2, sm: 3}, py: 1.25}}>
            <Typography component={'dt'} variant={'body1'} sx={{flexShrink: 0, color: tone.label}}>
                {label}
            </Typography>
            <Box component={'dd'} sx={{m: 0, minWidth: 0, textAlign: 'right'}}>
                <Typography component={'div'} variant={'body1'} sx={{fontWeight: 500, ...(amount ? AMOUNT : {overflowWrap: 'anywhere'})}}>
                    {value}
                </Typography>
                {!!caption && (
                    <Typography component={'div'} variant={'body2'} sx={{mt: 0.25, color: tone.label}}>
                        {caption}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export function ServiceRow({name, amount, caption}: {name: React.ReactNode, amount: string, caption?: string}) {
    const tone = useRowTone();

    return (
        <Box component={'li'} sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: {xs: 2, sm: 3}, py: 0.75}}>
            <Box sx={{minWidth: 0}}>
                <Typography variant={'body1'} sx={{fontWeight: 500, overflowWrap: 'anywhere'}}>{name}</Typography>
                {!!caption && <Typography variant={'body2'} sx={{color: tone.label}}>{caption}</Typography>}
            </Box>
            <Typography variant={'body1'} sx={{fontWeight: 500, ...AMOUNT}}>{amount}</Typography>
        </Box>
    );
}

export function GrandTotalRow({label, value}: {label: string, value: string}) {
    const theme = useTheme();

    return (
        <Box
            aria-live={'polite'}
            aria-atomic
            sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography component={'div'} variant={'subtitle1'} sx={{fontSize: 18}}>
                {label}
            </Typography>
            <Typography component={'div'} sx={{...AMOUNT, fontFamily: displayFontFamily(theme), fontSize: {xs: 30, sm: 34}, lineHeight: 1.15}}>
                {value}
            </Typography>
        </Box>
    );
}
